import React, { useEffect } from 'react';
import { Field, FieldArray, formValueSelector, reduxForm } from 'redux-form';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import cx from 'classnames';
import { useQuery } from '@apollo/client';
import { compose } from 'redux';
import { photosShow } from '../../helpers/photosShow';
import { getCurrencySymbol } from '../../helpers/currencyConvertion';
import { servicePlansUploadDir } from '../../config';
import CommonFormComponent from '../CommonField/CommonFormComponent';
import ImageUploadComponent from '../siteadmin/ImageUploadComponent/ImageUploadComponent';
import CommonImageDisplay from '../siteadmin/CommonImageDisplay/CommomImageDisplay';
import Loader from '../Loader/Loader';
import Link from '../Link/Link';
import FieldArrayComponent from '../CommonFields/FieldArrayComponent';
import validate from './validate';
import submit from './submit';
import { GoBackArrow } from '../Common/Assets/SvgIconComponent/CommonSvgIcon';
import { siteSettings } from '../../lib/graphql';
import { getCurrenciesData } from '../../actions/getCurrencies';
import { formatLocaleData } from '../../helpers/formatLocale';
import withStyles from 'isomorphic-style-loader/withStyles';
import messages from '../../locale/jsonMessages';
import cs from '../../components/commonStyle.css';


const AddServicePlanFormComponent = (props) => {

    const { submitting, error, handleSubmit, change, title, selectedLanguage } = props;

    const { formatMessage, locale } = useIntl();
    const dispatch = useDispatch();
    const selector = formValueSelector('AddServicePlanForm');
    const availableCurrencies = useSelector(state => state?.currency?.availableCurrencies);
    const servicePlanLoader = useSelector(state => state?.loader?.servicePlanLoader);
    const currency = useSelector(state => selector(state, 'currency'));
    const mediaImage = useSelector(state => selector(state, 'mediaImage'));

    const symbol = getCurrencySymbol(currency, locale);
    const path = photosShow(servicePlansUploadDir);

    useEffect(() => {
        async function fetchData() {
            await dispatch(getCurrenciesData());
        }
        fetchData();
    }, []);

    const { data, loading } = useQuery(siteSettings, {
        fetchPolicy: 'network-only',
        ssr: false,
        variables: { type: 'ai_features' }
    });

    const isAITextEnabled = !loading && data?.siteSettings?.find(item => item.name === 'aiTextGeneration')?.value === 'true';

    useEffect(() => {
        if (!isAITextEnabled && !loading) {
            change('aiFeaturesStatus', 'false');
        }
    }, [isAITextEnabled, loading]);

    const success = async (file, fromServer) => {
        change('mediaImage', fromServer.file.filename);
    };

    const options = [
        { value: 'renter', label: formatMessage(messages.guest) },
        { value: 'owner', label: formatMessage(messages.host) }
    ];

    const durationValue = [
        { value: '', label: formatMessage(messages.selectDuration) },
        ...Array.from({ length: 12 }, (_, i) => i + 1).map(month => ({
            value: String(month),
            label: month === 1
                ? `${month} ${formatMessage(messages.month)}`
                : `${month} ${formatMessage(messages.monthsLabel)}`
        }))
    ];

    const currenciesValue = [
        { value: '', label: formatMessage(messages.chooseCurrency) },
        ...(availableCurrencies
            ?.filter(c => c.isEnable)
            ?.map(c => ({ value: c.symbol, label: c.symbol })) || [])
    ];

    const aiFeatureValue = [
        { value: 'true', label: formatMessage(messages.enableLabel) },
        { value: 'false', label: formatMessage(messages.disableLabel) },
    ];

    return (
        <div className='siteAdminCenterContainer'>
            <div className={cx('singleBtnFlexEnd', cs.spaceBottom4)}>
                <Link to="/siteadmin/service-plans" className='goBackLink'>
                    <GoBackArrow />
                    <FormattedMessage {...messages.goBack} />
                </Link>
            </div>
            <div className="siteAdminCenterContainerBox">
                <form onSubmit={handleSubmit(submit)}>
                    {error && <strong>{formatMessage(error)}</strong>}
                    <h1 className='siteAdminHeading'>
                        {title === 'edit'
                            ? formatMessage(messages.editPlanLabel)
                            : formatMessage(messages.addPlanLabel)}
                    </h1>

                    <div className={cx('siteAdminDropZoneWrapper', cs.spaceBottom4)}>
                        <label className={cx('commonLabelText', cs.spaceBottom3)}>
                            <FormattedMessage {...messages.imageLabel} />
                        </label>
                        <CommonImageDisplay
                            loader={servicePlanLoader}
                            image={path + mediaImage}
                            isDefaultPic={!mediaImage}
                        />
                        <ImageUploadComponent
                            defaultMessage={formatMessage(messages.clickHeretoUploadLogo)}
                            componentConfig={{
                                iconFiletypes: ['.jpg', '.png', '.jpeg'],
                                multiple: false,
                                showFiletypeIcon: false,
                                postUrl: '/upload-plans'
                            }}
                            loaderName='servicePlanLoader'
                            success={success}
                            className={cs.spaceTop3}
                        />
                    </div>

                    <div className="oneColumnGrid">
                        <div className={cx(cs.commonInputGrpDivider, 'twoColumnGrid')}>
                            <Field
                                name="userType"
                                component={CommonFormComponent}
                                inputClass='commonSelectField'
                                labelText={formatMessage(messages.userTypeLabel)}
                                toolTipText={formatMessage(messages.userTypeToolTip)}
                            >
                                {options.map((item, key) => (
                                    <option key={key} value={item.value}>{item.label}</option>
                                ))}
                            </Field>
                            <Field
                                name="duration"
                                inputClass='commonSelectField'
                                component={CommonFormComponent}
                                labelText={formatMessage(messages.duration)}
                                toolTipText={formatMessage(messages.durationToolTip)}
                                isRequiredInput
                            >
                                {durationValue.map((item, key) => (
                                    <option key={key} value={item.value}>{item.label}</option>
                                ))}
                            </Field>
                            <Field
                                name="currency"
                                component={CommonFormComponent}
                                inputClass='commonSelectField'
                                labelText={formatMessage(messages.currency)}
                                toolTipText={formatMessage(messages.currencyToolTip)}
                                isRequiredInput
                            >
                                {currenciesValue.map((item, key) => (
                                    <option key={key} value={item.value}>{item.label}</option>
                                ))}
                            </Field>

                            <Field
                                name="price"
                                component={CommonFormComponent}
                                labelText={formatMessage(messages.priceLabel)}
                                placeholder={formatMessage(messages.priceLabel)}
                                prefixLabel={symbol}
                                maxLength={7}
                                isAddon
                                toolTipText={formatMessage(messages.priceToolTip)}
                                isRequiredInput
                            />
                            <Field
                                name="serviceFee"
                                component={CommonFormComponent}
                                labelText={formatMessage(messages.serviceFee)}
                                placeholder={formatMessage(messages.serviceFee)}
                                maxLength={6}
                                prefixLabel="%"
                                isAddon
                                toolTipText={formatMessage(messages.serviceFeeToolTip)}
                                isRequiredInput
                            />

                            {isAITextEnabled && (
                                <Field
                                    name="aiFeaturesStatus"
                                    component={CommonFormComponent}
                                    inputClass='commonSelectField'
                                    labelText={formatMessage(messages.aiFeatureLabel)}
                                    toolTipText={formatMessage(messages.aiFeatureToolTip)}
                                >
                                    {aiFeatureValue.map((item, key) => (
                                        <option key={key} value={item.value}>{item.label}</option>
                                    ))}
                                </Field>
                            )}
                        </div>
                        <FieldArray
                            name="dynamicServicePlans"
                            component={FieldArrayComponent}
                            activesFields={['language', 'planName', 'description']}
                            filedName="dynamicServicePlans"
                            selectedLanguage={selectedLanguage}
                        />
                    </div>
                    <div className={cx('singleBtnFlexEnd', cs.spaceTop4)}>
                        <Loader
                            type="button"
                            buttonType="submit"
                            show={loading}
                            disabled={submitting}
                            className={cs.btnPrimary}
                            label={formatMessage(messages.save)}
                        />
                    </div>
                </form>
            </div>
        </div>
    );
};

const mapState = (state) => ({
    servicePlanLoader: state.loader.servicePlanLoader,
    selectedLanguage: formValueSelector('AddServicePlanForm')(state, 'dynamicServicePlans'),
    activeLanguageCodes: state.runtime.isActiveLanguages.map(item => formatLocaleData(item))
});

export default compose(
    withStyles(cs),
    connect(mapState),
    reduxForm({ form: 'AddServicePlanForm', validate }),
    injectIntl,
)(AddServicePlanFormComponent);